import { useMemo, useState, useCallback } from 'react';
import type { Book, BookPage } from '../lib/api/types';
import { useExplorerStore } from './explorerStore';
import { ttsPlayer } from '../lib/audio/ttsPlayer';

interface WorldViewProps {
  book: Book;
  onExit: () => void;
}

const PAGE_COLORS = ['#4ade80', '#60a5fa', '#f472b6', '#fbbf24', '#a78bfa', '#34d399', '#f87171', '#22d3ee'];

function pageTitle(page: BookPage): string {
  return page.title || `第 ${page.page_number} 页`;
}

/**
 * 探索弹层：交互热点点击后打开，提供 AI 解说 + 页面内容 + 导航
 */
function ExploreModal({
  page,
  interaction,
  onClose,
  onContinue,
  bookSlug,
}: {
  page: BookPage;
  interaction: any;
  onClose: () => void;
  onContinue: () => void;
  bookSlug: string;
}) {
  const [speaking, setSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSpeak = useCallback(() => {
    if (speaking) {
      ttsPlayer.stop();
      setSpeaking(false);
      return;
    }
    const text = interaction.narration
      || interaction.description
      || `探索${interaction.title ?? interaction.target ?? '未知热点'}：${interaction.action ?? '点击触发交互动画'}`;
    setError(null);
    ttsPlayer.speak(text, {
      rate: 0.88,
      lang: 'zh-CN',
      onStart: () => setSpeaking(true),
      onEnd: () => setSpeaking(false),
      onError: (err) => {
        setSpeaking(false);
        setError(err instanceof Error ? err.message : '语音播放失败');
      },
    });
  }, [interaction, speaking]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(5,6,16,0.92)',
        backdropFilter: 'blur(14px)',
        display: 'flex',
        flexDirection: 'column',
        color: '#fff',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      {/* 顶栏 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 20px', borderBottom: '1px solid #ffffff22', flexShrink: 0 }}>
        <button
          onClick={() => { ttsPlayer.stop(); onClose(); }}
          style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '20px', padding: '4px' }}
        >
          ✕
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '14px', opacity: 0.6, marginBottom: '2px' }}>
            PAGE {page.page_number} · {pageTitle(page)}
          </div>
          <div style={{ fontSize: '17px', fontWeight: 700 }}>
            🔬 {interaction.title ?? interaction.target ?? '探索热点'}
          </div>
        </div>
      </div>

      {/* 主体 */}
      <div style={{ flex: 1, overflow: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* AI 解说播放区 */}
        <div
          style={{
            background: 'rgba(124,77,255,0.1)',
            border: '1.5px solid #7c4dff55',
            borderRadius: '16px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <div style={{ fontSize: '12px', color: '#a78bfa', letterSpacing: '1px' }}>🎙 AI 解说</div>
          <p style={{ margin: 0, lineHeight: 1.8, fontSize: '15px', color: '#e0e0ff' }}>
            {interaction.narration
              || interaction.description
              || `${interaction.title ?? interaction.target ?? '这个交互热点'}会触发${interaction.action ?? '一段有趣的交互动画'}，让故事更加生动。`}
          </p>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={handleSpeak}
              style={{
                padding: '12px 24px',
                borderRadius: '14px',
                border: 'none',
                background: speaking ? '#f472b6' : '#7c4dff',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: 600,
                minWidth: '120px',
              }}
            >
              {speaking ? '⏸ 停止' : '▶ 播放解说'}
            </button>
            {error && <span style={{ color: '#f87171', fontSize: '13px' }}>{error}</span>}
            {!ttsPlayer.isSupported && <span style={{ color: '#fbbf24', fontSize: '12px' }}>⚠ 浏览器不支持语音合成</span>}
          </div>
        </div>

        {/* 页面原文 */}
        <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '12px', padding: '16px 20px' }}>
          <div style={{ fontSize: '12px', color: '#ffffff77', marginBottom: '8px' }}>📖 页面原文</div>
          <p style={{ margin: 0, lineHeight: 1.8, fontSize: '14px', color: '#cccccc' }}>
            {page.content || '（本页暂无文字内容）'}
          </p>
        </div>

        {/* 交互动画提示（预留 Canvas 区域） */}
        <div
          style={{
            background: 'rgba(251,191,36,0.08)',
            border: '1px dashed #fbbf2466',
            borderRadius: '12px',
            padding: '20px',
            textAlign: 'center',
            color: '#fbbf24',
            fontSize: '13px',
          }}
        >
          ✨ 交互动画预览将在 Phase 3 接入（单元素动画播放 · 粒子爆发 · NPC 对话）
        </div>
      </div>

      {/* 底部导航 */}
      <div
        style={{
          display: 'flex',
          gap: '10px',
          padding: '14px 20px',
          borderTop: '1px solid #ffffff22',
          flexShrink: 0,
          background: 'rgba(5,6,16,0.8)',
        }}
      >
        <button
          onClick={() => { ttsPlayer.stop(); onClose(); }}
          style={{
            flex: 1,
            padding: '12px',
            borderRadius: '12px',
            border: '1px solid #ffffff33',
            background: 'none',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          🗺 返回世界图
        </button>
        <button
          onClick={() => { ttsPlayer.stop(); onContinue(); }}
          style={{
            flex: 1,
            padding: '12px',
            borderRadius: '12px',
            border: 'none',
            background: '#fbbf24',
            color: '#0b1220',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          🔍 继续探索
        </button>
        <a
          href={`/?mode=reader&book=${bookSlug}&page=${page.page_number}`}
          onClick={() => ttsPlayer.stop()}
          style={{
            flex: 1,
            padding: '12px',
            borderRadius: '12px',
            border: 'none',
            background: '#4ade80',
            color: '#0b1220',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
            textDecoration: 'none',
            textAlign: 'center',
          }}
        >
          📖 进入 3D 阅读
        </a>
      </div>
    </div>
  );
}

/**
 * 书籍世界探索视图（Phase 2 Week 8 · Tickets 03+04）：
 * - 页面作为世界节点横向展开，点击节点聚焦
 * - 交互热点点击打开弹层：AI 解说（TTS）+ 页面原文 + 交互动画预览占位
 * - 弹层导航：返回世界图 / 继续探索 / 进入 3D 阅读（携带 page 参数）
 * - 探索历史栈：push/pop 支持回退
 */
export function WorldView({ book, onExit }: WorldViewProps) {
  const { focusedPage, focusPage, exploreHistory, pushExploration, popExploration } = useExplorerStore();
  const [modalInteraction, setModalInteraction] = useState<any | null>(null);

  const pages = useMemo(
    () => [...(book.pages ?? [])].sort((a, b) => a.page_number - b.page_number),
    [book],
  );
  const focusedPageData = pages.find((p) => p.page_number === focusedPage) ?? pages[0];
  const lockedElements = useMemo(() => {
    if (!focusedPageData?.scene_3d?.elements) return [];
    return focusedPageData.scene_3d.elements;
  }, [focusedPageData]);
  const interactions = focusedPageData?.interactions ?? [];

  if (!focusedPageData) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
        此书暂无页面数据
      </div>
    );
  }

  return (
    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, #0d1030 0%, #141b45 55%, #1b2350 100%)', color: '#fff', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' }}>
      {/* 探索弹层 */}
      {modalInteraction && (
        <ExploreModal
          page={focusedPageData}
          interaction={modalInteraction}
          onClose={() => setModalInteraction(null)}
          onContinue={() => {
            setModalInteraction(null);
            // 自动聚焦下一交互热点（如果存在）
            const nextIdx = interactions.findIndex((it: any) => it === modalInteraction) + 1;
            if (nextIdx < interactions.length) {
              setModalInteraction(interactions[nextIdx]);
            }
          }}
          bookSlug={book.slug}
        />
      )}

      {/* 顶栏 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 20px', borderBottom: '1px solid #ffffff22' }}>
        <button
          onClick={onExit}
          style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid #ffffff33', color: '#fff', borderRadius: '10px', padding: '8px 14px', cursor: 'pointer', fontSize: '14px' }}
        >
          ← 返回星图
        </button>
        <h1 style={{ fontSize: '20px', margin: 0, flex: 1 }}>🌍 {book.title}</h1>
        <a
          href={`/?mode=reader&book=${book.slug}&page=${focusedPage}`}
          style={{ background: '#4ade80', color: '#0b1220', borderRadius: '10px', padding: '9px 16px', textDecoration: 'none', fontWeight: 600, fontSize: '14px' }}
        >
          📖 进入 3D 阅读
        </a>
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* 左侧：世界节点图 */}
        <div style={{ flex: 1.4, minWidth: 0, position: 'relative', overflow: 'auto', padding: '24px' }}>
          <div style={{ textAlign: 'center', color: '#ffffff77', fontSize: '13px', letterSpacing: '1px', marginBottom: '12px' }}>
            ✦ 探索故事世界 · 点击页面节点 ✦
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
            {pages.map((p, i) => {
              const color = PAGE_COLORS[i % PAGE_COLORS.length] ?? '#4ade80';
              const active = p.page_number === focusedPage;
              return (
                <button
                  key={p.page_number}
                  onClick={() => focusPage(p.page_number)}
                  title={pageTitle(p)}
                  style={{
                    width: '64px', height: '64px', borderRadius: '50%', border: 'none', cursor: 'pointer',
                    background: active ? `radial-gradient(circle at 35% 30%, #fff8, ${color})` : `radial-gradient(circle at 35% 30%, #ffffff33, ${color}66)`,
                    color: '#fff', fontSize: '15px', fontWeight: 700,
                    boxShadow: active ? `0 0 26px ${color}aa, inset 0 0 12px #00000044` : `0 4px 14px #00000066`,
                    transform: active ? 'scale(1.12)' : 'scale(1)',
                    transition: 'all 0.18s ease',
                  }}
                >
                  {p.page_number}
                </button>
              );
            })}
            {/* 探索历史指示 */}
            {exploreHistory.length > 0 && (
              <button
                onClick={popExploration}
                style={{ background: '#7c4dff55', border: '1px solid #a78bfa88', color: '#fff', borderRadius: '18px', padding: '9px 16px', cursor: 'pointer', fontSize: '13px' }}
              >
                ↺ 返回上一探索（{exploreHistory.length}）
              </button>
            )}
          </div>

          {/* 当前页内容摘要 */}
          <div style={{ marginTop: '28px', background: 'rgba(255,255,255,0.05)', border: '1px solid #ffffff22', borderRadius: '16px', padding: '20px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <span style={{ background: '#4ade8033', color: '#4ade80', borderRadius: '20px', padding: '3px 10px', fontSize: '12px' }}>
                PAGE {focusedPageData.page_number}
              </span>
              <h2 style={{ fontSize: '17px', margin: 0 }}>{pageTitle(focusedPageData)}</h2>
            </div>
            <p style={{ color: '#dddddd', lineHeight: 1.8, fontSize: '14px', margin: 0 }}>
              {focusedPageData.content || '（本页暂无文字内容）'}
            </p>
          </div>
        </div>

        {/* 右侧：探索面板 */}
        <div style={{ width: '300px', minWidth: '260px', borderLeft: '1px solid #ffffff22', padding: '20px', overflow: 'auto', background: 'rgba(0,0,0,0.18)' }}>
          <div style={{ fontSize: '13px', color: '#ffffff99', letterSpacing: '1px', marginBottom: '12px' }}>🔬 可探索元素</div>

          <div style={{ marginBottom: '18px' }}>
            <div style={{ fontSize: '12px', color: '#ffffff77', marginBottom: '8px' }}>· 3D 场景元素</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {lockedElements.length === 0 && <span style={{ color: '#ffffff55', fontSize: '13px' }}>无</span>}
              {lockedElements.map((el: any, i: number) => (
                <span key={i} style={{ background: '#60a5fa22', border: '1px solid #60a5fa66', color: '#93c5fd', borderRadius: '8px', padding: '5px 10px', fontSize: '13px' }}>
                  {el.id ?? el.name ?? `element-${i}`}
                </span>
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', color: '#ffffff77', marginBottom: '8px' }}>· 交互热点（点击深入）</div>
            {interactions.length === 0 && <span style={{ color: '#ffffff55', fontSize: '13px' }}>无</span>}
            {interactions.map((it: any, i: number) => (
              <button
                key={i}
                onClick={() => {
                  pushExploration(focusedPageData.page_number);
                  setModalInteraction(it);
                }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', marginBottom: '10px',
                  background: 'rgba(255,255,255,0.06)', border: '1px solid #fbbf2444', color: '#fcd34d',
                  borderRadius: '12px', padding: '12px 14px', cursor: 'pointer', fontSize: '13px',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#fbbf2418')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
              >
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>✨ {it.title ?? it.target ?? `热点 ${i + 1}`}</div>
                <div style={{ color: '#ffffff88', fontSize: '12px' }}>{it.action ?? it.description ?? '点击触发交互动画'}</div>
              </button>
            ))}
          </div>

          {exploreHistory.length > 0 && (
            <div style={{ marginTop: '16px', background: '#7c4dff1a', border: '1px dashed #a78bfa66', borderRadius: '12px', padding: '12px 14px', fontSize: '12px', color: '#c4b5fd' }}>
              你正在从 PAGE {exploreHistory[exploreHistory.length - 1]} 深入探索到此页 —— 点击交互热点可收听 AI 解说。
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
